import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  resetAllStores,
  handleSessionTransition,
  clearSessionTracking
} from './storeResetUtils';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  contact?: string;
  profilePhoto?: string;
  role: string;
  createdAt: string;
  flag_daily_update?: number; // 0 = mandatory daily update, 1 = optional
  is_super_admin?: number; // 1 = super admin, 0 or undefined = regular user
  is_manager?: boolean | number; // Manager flag - determines if user can manage a team
  department_id?: number | string; // OKR: Department context
  dept_id?: number | string; // Alias for department_id
  department_name?: string; // Department name for SSO / app visibility
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (user: User, token: string, refreshToken?: string) => void;
  logout: () => void;
  clearError: () => void;
  initializeAuth: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (user: User, token: string, refreshToken?: string) => {
        console.log('🔐 AuthStore: Login process started');
        console.log('👤 AuthStore: User data received:', user);
        console.log('🔑 AuthStore: Token received:', token ? 'Token present' : 'No token');
        console.log('🔄 AuthStore: Refresh token received:', refreshToken ? 'Refresh token present' : 'No refresh token');

        // Check for session conflict and reset stores if different user
        const hadConflict = handleSessionTransition(user.id);
        if (hadConflict) {
          console.log('🔄 AuthStore: Session conflict detected, stores have been reset');
        }

        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        localStorage.setItem('authToken', token);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAuthenticated', 'true');

        console.log('✅ AuthStore: User logged in successfully');
        console.log('📝 AuthStore: User name set to:', user.name);
        console.log('💾 AuthStore: Data saved to localStorage');
      },

      logout: () => {
        console.log('AuthStore: logout called');

        // Reset all application stores before clearing auth
        console.log('AuthStore: Resetting all application stores...');
        resetAllStores();

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');

        // Clear session tracking
        clearSessionTracking();

        console.log('AuthStore: User logged out successfully, all stores reset');
      },

      clearError: () => {
        set({ error: null });
      },

      initializeAuth: () => {
        console.log('AuthStore: initializeAuth called');
        set({ isLoading: true });

        // Check localStorage for existing authentication data
        const token = localStorage.getItem('authToken');
        const userJson = localStorage.getItem('user');
        const isAuthenticated = localStorage.getItem('isAuthenticated');

        console.log('AuthStore: Checking localStorage:', {
          token: token ? 'Present' : 'Missing',
          user: userJson ? 'Present' : 'Missing',
          isAuthenticated: isAuthenticated
        });

        if (token && userJson && isAuthenticated === 'true') {
          try {

            const user = JSON.parse(userJson);

            // Check if token is expired (basic check)
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              try {
                const payload = JSON.parse(atob(tokenParts[1]));
                const currentTime = Math.floor(Date.now() / 1000);

                console.log('AuthStore: Token expiry check:', {
                  exp: payload.exp,
                  currentTime: currentTime,
                  isExpired: payload.exp < currentTime
                });

                if (payload.exp && payload.exp < currentTime) {
                  console.log('AuthStore: Token expired, clearing auth data');
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('user');
                  localStorage.removeItem('isAuthenticated');
                  set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                  });
                  return;
                }
              } catch (error) {
                console.error('AuthStore: Error parsing token payload:', error);
                // Clear invalid token
                localStorage.removeItem('authToken');
                localStorage.removeItem('user');
                localStorage.removeItem('isAuthenticated');
                set({
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                });
                return;
              }
            }

            // Convert relative photo URL to full URL if needed
            if (user.profilePhoto && !user.profilePhoto.startsWith('http')) {
              user.profilePhoto = `http://localhost:5000${user.profilePhoto}`;
              console.log('🖼️ AuthStore: Converted profile photo URL:', user.profilePhoto);
            }

            console.log('AuthStore: Found valid auth data, restoring state');
            console.log('AuthStore: User object from localStorage:', {
              id: user.id,
              name: user.name,
              email: user.email,
              profilePhoto: user.profilePhoto,
              contact: user.contact
            });
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            console.error('AuthStore: Error parsing user data from localStorage:', error);
            // Clear invalid data
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          console.log('AuthStore: No valid auth data found, starting unauthenticated');
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
